import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * PO-2026-06 #7 — Event Recap AI review card (admin).
 * Shows the GPT-5.6 recap analysis for a shift: status, summary, findings,
 * with Reanalyze + Resolve. Never decides pay — flags for a human only.
 */
const SEV = {
  high: 'bg-rose-100 text-rose-700 border-rose-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const EventRecapAiCard = ({ shiftId }) => {
  const [review, setReview] = useState(undefined); // undefined=loading, null=none
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/event-recap-ai/${shiftId}`);
      setReview(r.data?.review || null);
    } catch (_) { setReview(null); }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/event-recap-ai/${shiftId}/reanalyze`);
      await load();
      toast.success('Event Recap AI analysis complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analysis failed');
    } finally { setBusy(false); }
  };

  const resolve = async (status) => {
    try {
      await api.post(`/admin/event-recap-ai/${shiftId}/resolve`, { review_status: status });
      toast.success(status === 'resolved' ? 'Marked resolved' : 'Acknowledged');
      load();
    } catch (e) { toast.error('Failed to update'); }
  };

  if (review === undefined) {
    return (
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="event-recap-ai-card">
        <Loader2 className="w-4 h-4 animate-spin text-indigo-500 inline mr-2" />
        <span className="text-sm text-indigo-700">Loading Event Recap AI…</span>
      </div>
    );
  }

  const isClean = review && review.status === 'clean';
  const needsReview = review && review.status === 'needs_review';

  return (
    <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-white to-indigo-50/40 p-4" data-testid="event-recap-ai-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">Event Recap AI</div>
            <div className="text-[11px] text-slate-500">GPT-5.6 review · flags for human decision, never auto-decides pay</div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy}
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
          data-testid="event-recap-ai-reanalyze-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          {review ? 'Reanalyze' : 'Analyze Event'}
        </Button>
      </div>

      {busy && (
        <p className="text-[11px] text-indigo-500 mb-2" data-testid="event-recap-ai-analyzing">
          <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Analyzing the full event record… this can take up to a minute.
        </p>
      )}
      {!review ? (
        <p className="text-sm text-slate-500" data-testid="event-recap-ai-none">
          No AI review yet. It runs automatically once the recap is submitted, or tap Analyze Event.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="event-recap-ai-status">
            {isClean ? (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 gap-1"><ShieldCheck className="w-3 h-3" /> AI reviewed — no issues found</Badge>
            ) : needsReview ? (
              <Badge className="bg-rose-100 text-rose-700 border border-rose-200 gap-1"><AlertTriangle className="w-3 h-3" /> Needs admin review</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Review incomplete</Badge>
            )}
            {typeof review.confidence === 'number' && review.confidence > 0 && (
              <span className="text-[11px] text-slate-400">confidence {(review.confidence * 100).toFixed(0)}%</span>
            )}
            {review.review_status && needsReview && (
              <Badge className="bg-white text-slate-500 border border-slate-200 text-[10px]">{review.review_status}</Badge>
            )}
          </div>

          {review.summary && (
            <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap" data-testid="event-recap-ai-summary">{review.summary}</p>
          )}

          {review.findings && review.findings.length > 0 && (
            <div className="space-y-1.5 mb-3" data-testid="event-recap-ai-findings">
              {review.findings.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Badge className={`${SEV[f.severity] || SEV.low} border text-[9px] uppercase shrink-0 mt-0.5`}>{f.severity}</Badge>
                  <span className="text-slate-700">{f.detail}
                    <span className="text-[10px] text-slate-400 ml-1">({f.source === 'system' ? 'auto-check' : 'AI'})</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {needsReview && review.review_status === 'open' && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700"
                onClick={() => resolve('resolved')} data-testid="event-recap-ai-resolve-btn">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark resolved
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-500"
                onClick={() => resolve('acknowledged')} data-testid="event-recap-ai-ack-btn">
                Acknowledge
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventRecapAiCard;
